import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import DashNav from "@/components/chalann/DashNav";
import { statsToday, listPriceAlerts, applyPriceAlert, dismissPriceAlert, simulatePriceAlert, voiceTelemetry, formatMXN, getStore, getUser, isOnePay } from "@/lib/api";
import { Mic, Package, BookMarked, ArrowUpRight, TrendingUp, AlertTriangle, CreditCard, Sparkles, ScanBarcode, BookOpen } from "lucide-react";
import { toast, Toaster } from "sonner";
import PriceAlertCard from "@/components/chalann/PriceAlertCard";
import InstallPrompt from "@/components/chalann/InstallPrompt";
import TrialBanner from "@/components/chalann/TrialBanner";
import OnboardingChecklist from "@/components/chalann/OnboardingChecklist";

export default function Dashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [applyingId, setApplyingId] = useState(null);
  const [simulating, setSimulating] = useState(false);
  const [telemetry, setTelemetry] = useState(null);
  const store = getStore();
  const user = getUser();
  const isStaff = user?.role === "staff";
  const onePay = isOnePay();

  useEffect(() => {
    if (!store?.id) {
      nav("/setup");
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const [s, a, t] = await Promise.all([statsToday(), listPriceAlerts(), onePay ? Promise.resolve(null) : voiceTelemetry()]);
        if (!cancelled) { setStats(s); setAlerts(a); setTelemetry(t); }
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, [nav, store?.id, onePay]);

  const refresh = async () => {
    try {
      const [s, a, t] = await Promise.all([statsToday(), listPriceAlerts(), onePay ? Promise.resolve(null) : voiceTelemetry()]);
      setStats(s); setAlerts(a); setTelemetry(t);
    } catch { /* noop */ }
  };

  const handleApply = async (alert) => {
    setApplyingId(alert.id);
    try {
      const r = await applyPriceAlert(alert.id);
      toast.success(
        `Precios actualizados · ${r.updated_products} producto${r.updated_products !== 1 ? "s" : ""} · +${formatMXN(r.delta_mxn)}`
      );
      refresh();
    } catch (e) {
      toast.error("No se pudo aplicar la alerta");
    } finally { setApplyingId(null); }
  };

  const handleDismiss = async (alert) => {
    try {
      await dismissPriceAlert(alert.id);
      toast("Alerta ignorada", { description: `${alert.provider} · +${alert.pct_change}%` });
      refresh();
    } catch { toast.error("No se pudo ignorar la alerta"); }
  };

  const handleSimulate = async () => {
    setSimulating(true);
    try {
      await simulatePriceAlert();
      toast.success("Alerta IA generada", { description: "Nuevo aviso de precio de tu distribuidor" });
      refresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || "No se pudo simular";
      toast.error(msg);
    } finally { setSimulating(false); }
  };

  const bigButtons = [
    {
      key: "cobrar",
      label: "Cobrar",
      sub: onePay ? "Pistola · Buscador · Cámara" : "Voz · Cámara · QR",
      to: "/cobrar",
      icon: onePay ? ScanBarcode : Mic,
      color: "#ff5a00",
      accent: "text-[#ff5a00]",
    },
    {
      key: "inventario",
      label: "Inventario",
      sub: "Existencias y altas",
      to: "/inventario",
      icon: Package,
      color: "#ccff00",
      accent: "text-[#ccff00]",
    },
    {
      key: "fiados",
      label: "Cuentas por Cobrar",
      sub: "Fiados pendientes",
      to: "/fiados",
      icon: BookMarked,
      color: "#f5f5f5",
      accent: "text-white",
    },
    ...(onePay ? [{
      key: "guia",
      label: "Guía",
      sub: "Pistola · operación · inventario",
      to: "/guia",
      icon: BookOpen,
      color: "#ccff00",
      accent: "text-[#ccff00]",
    }] : []),
  ];

  return (
    <main className="min-h-screen bg-[#050505] text-white" data-testid="dashboard">
      <div className="grain-overlay" />
      <DashNav />

      <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 py-8 md:py-12">
        <TrialBanner />
        <InstallPrompt />
        <OnboardingChecklist />
        {/* Header row */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <p className="font-mono text-[11px] tracking-[0.3em] text-white/40 uppercase mb-2">
              / Panel de {store?.name || "tu tienda"}{onePay ? " · Plan ONE PAY" : ""}
            </p>
            <h1 className="font-display text-4xl md:text-6xl tracking-tighter leading-none">
              Hoy es un <span className="text-[#ff5a00]">buen día</span> <br className="md:hidden"/>para vender.
            </h1>
          </div>
          <p className="font-mono text-xs text-white/40 tracking-widest">
            {new Date().toLocaleDateString("es-MX", {
              weekday: "long", day: "2-digit", month: "long", year: "numeric",
            }).toUpperCase()}
          </p>
        </div>

        {/* HERO METRIC: Ventas del día */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-[#ff5a00]/[0.15] via-[#ff5a00]/[0.05] to-transparent p-8 md:p-12 overflow-hidden"
          data-testid="ventas-dia-card"
        >
          <div className="glow-orange" style={{ bottom: "-60%", right: "-30%" }} />
          <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="rec-dot" />
                <span className="font-mono text-[11px] tracking-widest text-white/60 uppercase">Ventas del día · en vivo</span>
              </div>
              <p
                className="font-display text-6xl sm:text-7xl md:text-8xl lg:text-9xl tracking-tighter leading-none"
                data-testid="ventas-dia-total"
              >
                {loading ? "—" : formatMXN(stats?.ventas_dia_total)}
              </p>
              <p className="mt-3 text-white/50 text-sm font-mono uppercase tracking-widest">
                {stats?.ventas_dia_count || 0} tickets · {stats?.items_vendidos || 0} productos
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3 md:gap-4 min-w-[280px]">
              <MiniStat label="Efectivo" value={formatMXN(stats?.efectivo)} icon={TrendingUp} color="#ccff00" />
              <MiniStat label="Tarjeta" value={formatMXN(stats?.tarjeta)} icon={CreditCard} color="#f5f5f5" />
              <MiniStat label="Fiado" value={formatMXN(stats?.fiado_hoy)} icon={AlertTriangle} color="#ff5a00" />
            </div>
          </div>
        </motion.section>

        {/* AI Price alerts */}
        {alerts.length > 0 && (
          <div className="mt-6 space-y-3" data-testid="price-alerts">
            {alerts.map((a) => (
              <PriceAlertCard
                key={a.id}
                alert={a}
                onApply={handleApply}
                onDismiss={handleDismiss}
                loadingApply={applyingId === a.id}
              />
            ))}
          </div>
        )}

        {/* Alerts row */}
        {!isStaff && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <AlertPill
              label="Fiado pendiente total"
              value={formatMXN(stats?.fiado_pendiente_total)}
              action="Cobrar →"
              onClick={() => nav("/fiados")}
              color="#ff5a00"
              testid="alert-fiados"
            />
            <AlertPill
              label="Productos bajo stock"
              value={`${stats?.productos_bajo_stock || 0} artículos`}
              action="Revisar →"
              onClick={() => nav("/inventario")}
              color="#ccff00"
              testid="alert-stock"
            />
          </div>
        )}

        {/* BIG BUTTONS */}
        <div className="mt-16 md:mt-24">
          <p className="font-mono text-[11px] tracking-[0.3em] text-white/40 uppercase mb-4">
            / ¿Qué vamos a hacer?
          </p>
          <div className={`grid grid-cols-1 gap-4 md:gap-5 ${bigButtons.length === 4 ? "md:grid-cols-2 xl:grid-cols-4" : "md:grid-cols-3"}`}>
            {bigButtons.map((b, i) => {
              const Icon = b.icon;
              return (
                <motion.button
                  key={b.key}
                  onClick={() => nav(b.to)}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.1 * i, ease: [0.22, 1, 0.36, 1] }}
                  className="group relative overflow-hidden text-left rounded-3xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] p-8 md:p-10 min-h-[240px] md:min-h-[280px] flex flex-col justify-between"
                  style={{ transitionProperty: "background-color, border-color, transform", transitionDuration: "400ms" }}
                  data-testid={`big-btn-${b.key}`}
                >
                  <div className="flex items-start justify-between">
                    <Icon size={44} strokeWidth={1.5} style={{ color: b.color }} />
                    <ArrowUpRight
                      size={22}
                      className="text-white/40 group-hover:text-white group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-300"
                    />
                  </div>
                  <div>
                    <p className="font-display text-4xl md:text-5xl tracking-tighter leading-none" style={{ color: b.color }}>
                      {b.label}
                    </p>
                    <p className="mt-2 font-mono text-[11px] tracking-widest text-white/50 uppercase">{b.sub}</p>
                  </div>

                  <div
                    className="absolute -bottom-24 -right-24 w-52 h-52 rounded-full opacity-0 group-hover:opacity-30 blur-3xl"
                    style={{ backgroundColor: b.color, transitionProperty: "opacity", transitionDuration: "400ms" }}
                  />
                </motion.button>
              );
            })}
          </div>
        </div>

        <p className="mt-14 text-center text-white/40 text-xs font-mono tracking-widest uppercase">
          {onePay
            ? <>Consejo: conecta tu pistola Bluetooth y presiona <span className="text-[#ff5a00]">Cobrar</span>. Sin IA, sin costos ocultos.</>
            : <>Consejo: presiona <span className="text-[#ff5a00]">Cobrar</span> y dile a CHALANN qué te están pidiendo.</>}
        </p>

        {!onePay && (
        <div className="mt-6 flex flex-col items-center gap-3">
          <button
            onClick={handleSimulate}
            disabled={simulating}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 hover:border-[#ccff00]/50 text-white/60 hover:text-[#ccff00] px-4 py-2 text-xs font-mono uppercase tracking-widest transition-colors duration-200 disabled:opacity-50"
            data-testid="simulate-price-alert"
          >
            <Sparkles size={12} /> {simulating ? "Generando…" : "Simular alerta IA de precios"}
          </button>
          {telemetry && telemetry.total_calls > 0 && (
            <div
              className="text-[10px] font-mono uppercase tracking-widest text-white/40 flex flex-wrap justify-center gap-3"
              data-testid="voice-telemetry-mini"
            >
              <span>IA voz · {telemetry.total_calls} comandos</span>
              <span className="text-[#ccff00]">avg {telemetry.avg_tokens} tk</span>
              <span>· {telemetry.zero_token_calls} sin tokens</span>
              <span>· {telemetry.under_400_calls}/{telemetry.total_calls} bajo meta 400</span>
            </div>
          )}
        </div>
        )}
      </div>
      <Toaster theme="dark" position="top-center" />
    </main>
  );
}

function MiniStat({ label, value, icon: Icon, color }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/40 p-3 md:p-4">
      <div className="flex items-center justify-between mb-2">
        <Icon size={14} style={{ color }} />
        <span className="font-mono text-[9px] tracking-widest text-white/40 uppercase">{label}</span>
      </div>
      <p className="font-display text-lg md:text-xl tracking-tighter">{value}</p>
    </div>
  );
}

function AlertPill({ label, value, action, onClick, color, testid }) {
  return (
    <button
      onClick={onClick}
      className="group text-left rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] p-4 md:p-5 flex items-center justify-between transition-colors duration-300"
      data-testid={testid}
    >
      <div>
        <p className="font-mono text-[10px] tracking-widest uppercase text-white/50">{label}</p>
        <p className="font-display text-2xl md:text-3xl tracking-tighter mt-1" style={{ color }}>{value}</p>
      </div>
      <span className="font-mono text-xs uppercase tracking-widest text-white/50 group-hover:text-white transition-colors">
        {action}
      </span>
    </button>
  );
}
